import React, { useEffect, useState } from "react";
import { X, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";

const STORAGE_KEY = "primenax_newsletter_popup_v1";
const SHOW_DELAY_MS = 8000; // first appearance after 8s on the page
const DISMISS_REMIND_DAYS = 7; // after dismissing, don't show again for a week

/**
 * Dark-luxury email capture popup. Shows once per visitor (or every 7 days
 * after dismiss). Triggers after 12s on the page, OR immediately on
 * exit-intent (cursor leaves the top of the viewport on desktop).
 *
 * On submit, hits POST /api/newsletter — same endpoint as the footer
 * signup form — and rewards the user with the live Stripe WELCOME10
 * promo code.
 */
const NewsletterPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // ---- Eligibility + scheduling ----
  useEffect(() => {
    let cancelled = false;

    const eligible = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return true;
        const data = JSON.parse(raw);
        if (data.subscribed) return false;
        if (data.dismissedAt) {
          const ageMs = Date.now() - data.dismissedAt;
          return ageMs > DISMISS_REMIND_DAYS * 24 * 60 * 60 * 1000;
        }
        return true;
      } catch {
        return true;
      }
    };

    if (!eligible()) return;

    const show = () => {
      if (cancelled) return;
      setOpen(true);
    };

    const timer = setTimeout(show, SHOW_DELAY_MS);

    const onMouseLeave = (e) => {
      // Exit-intent: cursor leaves viewport through the top
      if (e.clientY <= 0 && !cancelled) {
        show();
        document.removeEventListener("mouseleave", onMouseLeave);
      }
    };
    document.addEventListener("mouseleave", onMouseLeave);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  // ---- Lock body scroll when open ----
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  const persistDismiss = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ dismissedAt: Date.now() })
      );
    } catch {}
  };

  const persistSubscribed = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ subscribed: true, at: Date.now() })
      );
    } catch {}
  };

  const close = () => {
    setOpen(false);
    if (!success) persistDismiss();
  };

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await subscribeNewsletter(trimmed);
      setSuccess(true);
      persistSubscribed();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Couldn't subscribe. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      data-testid="newsletter-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="newsletter-popup-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={close}
        data-testid="newsletter-popup-backdrop"
      />

      {/* Card */}
      <div className="relative w-full max-w-md md:max-w-lg bg-[#0A0A0A] border border-white/10 shadow-2xl">
        {/* Crimson top stripe */}
        <div className="h-1 bg-[#D10000]" />

        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="newsletter-popup-close"
          className="absolute top-4 right-4 z-10 text-white/50 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-8 md:p-12 text-left">
          {success ? (
            <div data-testid="newsletter-popup-success">
              <CheckCircle2 className="h-10 w-10 text-[#D10000] mb-6" />
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-3">
                YOU'RE IN
              </div>
              <h2
                id="newsletter-popup-title"
                className="font-display uppercase leading-[0.9] tracking-tight mb-5"
                style={{ fontSize: "clamp(1.75rem, 4vw, 2.5rem)" }}
              >
                Welcome to the inner circle.
              </h2>
              <p className="text-white/65 leading-relaxed mb-6">
                Use code{" "}
                <span className="font-display tracking-widest text-[#D10000] text-lg">
                  WELCOME10
                </span>{" "}
                at checkout for 10% off your first order. Check your inbox —
                drops and stack secrets land there first.
              </p>
              <button
                type="button"
                onClick={close}
                data-testid="newsletter-popup-done-button"
                className="inline-flex items-center gap-2 bg-[#D10000] hover:bg-[#A30000] text-white font-display tracking-widest uppercase px-6 py-3 transition-colors"
              >
                Got it
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-3">
                FIRST-TIMER OFFER
              </div>
              <h2
                id="newsletter-popup-title"
                className="font-display uppercase leading-[0.9] tracking-tight mb-5"
                style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}
              >
                10% off your <br />
                <span className="text-stroke">first stack.</span>
              </h2>
              <p className="text-white/65 leading-relaxed mb-7">
                Drop your email. We send drops, restocks, and stack protocols
                — never spam. Coupon hits your inbox instantly.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <input
                  type="email"
                  required
                  placeholder="you@dominate.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="newsletter-popup-email-input"
                  className="w-full bg-transparent border border-white/15 focus:border-[#D10000] outline-none px-4 py-4 text-white placeholder:text-white/35 tracking-wide"
                />
                <button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  data-testid="newsletter-popup-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D10000] hover:bg-[#A30000] disabled:opacity-40 text-white font-display text-lg tracking-widest uppercase py-4 transition-colors"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Claim My 10% <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>

              <button
                type="button"
                onClick={close}
                data-testid="newsletter-popup-dismiss-link"
                className="mt-5 text-white/40 hover:text-white/70 text-xs tracking-widest uppercase transition-colors"
              >
                No thanks — I'll pay full price
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
